import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import Button from 'flarum/common/components/Button';
import splitCoverImages from '../utils/splitCoverImages';
import editPostButton from '../utils/editPostButton';

// app.modal.show()'s own first parameter type (UnsafeModalClass |
// AsyncModalClass) isn't exported from flarum/core - extracted through
// Parameters<> instead of naming it directly, so this stays correct even
// if that internal type is ever renamed.

// A static `import ... from 'ext:champis-species/...'` captures whatever
// flarum.reg holds at *this module's own* evaluation time, which isn't
// guaranteed to be after champis-species has finished registering its own
// exports (see champis-forum's DiscussionFilters.js, the other place this
// exact lazy-lookup pattern is used, for the confirmed failure that reusing
// a static import hit). Resolved lazily instead, at actual render time.
//
// Typed as `typeof Page` (any real Component subclass works as a JSX tag
// type - Page is already imported above, no need for a separate Component
// import just for this) rather than Mithril.ComponentTypes: a bare
// ComponentTypes union has no construct/call signature TypeScript's own JSX
// checker recognizes, so `<SpeciesSearchWidget />` below fails to compile
// with it (confirmed directly - "does not have any construct or call
// signatures").
function getSpeciesSearchWidget() {
  return window.flarum.reg.get('champis-species', 'forum/components/SpeciesSearchWidget');
}

// champis-forum's startDiscussion(): the composer with this page's category
// preselected (or the log-in modal) - looked up at click time, same reason
// as getSpeciesSearchWidget() above.
function startDiscussion(tagSlug) {
  window.flarum.reg.get('champis-forum', 'forum/utils/startDiscussion')?.(tagSlug);
}

// URL category -> forum tag slug - same as BookSelection::CATEGORY_TAGS.
const CATEGORY_TAGS = {
  field: 'livres-de-terrain',
  reference: 'livres-de-reference',
  mycology: 'livres-de-mycologie-generale'
};

/**
 * /books/:category - the discussions of one of the three "Livres" sub-tags
 * chosen in the extension's settings, in their order there (forum payload's
 * `booksSelection`, see BookSelection.php), one per book (title = book
 * title + author, first post = editor/
 * page count/ISBN/back-cover description, free-text, written by Christian
 * Frund) - matching champis.net/books/:category. Rendered as a list of
 * mini-articles (title + post body, same as ArticlePage.js) rather than
 * parsed into separate fields: the post body's formatting isn't actually
 * consistent across books (some have all of Editeur/Nb pages/ISBN/
 * Description labeled, others just Editeur + plain paragraphs - confirmed
 * by checking the real posts), so rendering it as-is is both simpler and
 * more robust than a label-based parser that would silently drop content
 * whenever a book doesn't follow the usual shape.
 *
 * Only a handful of books per category (4/1/1 so far): the whole forum is
 * loaded, then the chosen ones picked out of it.
 */
export default class BooksPage extends Page {
  category = '';
  discussions = null;
  error = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.category = m.route.param('category');
    this.discussions = null;
    this.error = false;
    this.load();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    const category = m.route.param('category');
    if (category !== this.category) {
      this.category = category;
      this.load();
    }
  }
  categoryTitle() {
    return app.translator.trans(`champis-others.forum.books_page.title_${this.category}`);
  }
  load() {
    this.discussions = null;
    this.error = false;
    app.setTitle(this.categoryTitle());
    const category = this.category;
    const tag = CATEGORY_TAGS[category];
    const chosen = (app.forum.attribute('booksSelection') || {})[category] || [];
    if (!tag || chosen.length === 0) {
      this.discussions = [];
      m.redraw();
      return;
    }
    this.loadForum(tag).then(discussions => {
      // Another category opened in the meantime: its own load() shows it.
      if (category !== this.category) return;
      const byId = new Map(discussions.map(discussion => [discussion.id(), discussion]));
      this.discussions = chosen.map(id => byId.get(id)).filter(discussion => !!discussion);
      m.redraw();
    }).catch(() => {
      if (category !== this.category) return;
      this.error = true;
      m.redraw();
    });
  }

  // Every page of the forum's discussions (50 at most per request), with
  // their first post.
  async loadForum(tag) {
    const discussions = [];
    let url = app.forum.attribute('apiUrl') + '/discussions';
    let params = {
      filter: {
        tag
      },
      page: {
        limit: 50
      },
      include: 'firstPost'
    };
    while (url) {
      const payload = await app.request({
        method: 'GET',
        url,
        params
      });
      discussions.push(...app.store.pushPayload(payload));

      // The next page's URL carries every parameter already.
      url = payload.links?.next;
      params = undefined;
    }
    return discussions;
  }
  view() {
    const rows = this.discussions || [];
    const SpeciesSearchWidget = getSpeciesSearchWidget();
    return m("div", {
      className: "Page BooksPage"
    }, m("header", {
      className: "Hero BooksHero"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "containerNarrow"
    }, m("h1", {
      className: "Hero-title"
    }, m("i", {
      className: "icon fas fa-book-open",
      "aria-hidden": "true"
    }), " ", this.categoryTitle())))), m("div", {
      className: "container"
    }, m("div", {
      className: "Page-container"
    }, m("div", {
      className: "Page-content"
    }, m("div", {
      className: "ChampisBooksList"
    }, !this.discussions && this.error ? m(Placeholder, {
      text: app.translator.trans('champis-others.forum.books_page.error')
    }) : !this.discussions ? m(LoadingIndicator, null) : rows.length === 0 ? m(Placeholder, {
      text: app.translator.trans('champis-others.forum.books_page.empty')
    }) : rows.map(discussion => this.book(discussion)))), m("div", {
      className: "Page-sidebar"
    }, SpeciesSearchWidget && m(SpeciesSearchWidget, null), this.newDiscussionButton()))));
  }

  // Same button (icon, classes, disabled/login-fallback logic, core's own
  // translator keys) as core's IndexSidebar.tsx renders on a tag page's own
  // sidebar - a plain copy rather than reusing IndexSidebar itself, which
  // would need a routeName ('index'/'tag') this page doesn't have to get
  // past champis-forum's own restrictSidebarWidgetsToIndexPages.js gate.
  // Same pattern as champis-species' DescriptionsPage.js/champis-keys'
  // DeterminationKeysPage.js own copy of this button.
  newDiscussionButton() {
    const canStartDiscussion = app.forum.attribute('canStartDiscussion') || !app.session.user;
    return m(Button, {
      icon: "fas fa-edit",
      className: "Button Button--primary IndexPage-newDiscussion",
      "aria-label": app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`),
      onclick: () => startDiscussion(CATEGORY_TAGS[this.category]),
      disabled: !canStartDiscussion
    }, app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`));
  }
  book(discussion) {
    const post = discussion.firstPost();
    const {
      images,
      text
    } = post ? splitCoverImages(post.contentHtml() || '') : {
      images: [],
      text: ''
    };
    return m("div", {
      key: discussion.id(),
      className: "ChampisBooksList-item",
      onclick: e => this.handleBookClick(e, discussion, post)
    }, m("h2", {
      className: "ChampisBooksList-title"
    }, discussion.title(), " ", editPostButton(post)), !post ? m(LoadingIndicator, null) : m('[', null, images.length > 0 && m("div", {
      className: "ChampisBooksList-images"
    }, images.map(image => m.trust(image))), m("div", {
      className: "Post-body"
    }, m.trust(text))));
  }

  // Delegated for both of fof/upload's own inline attachment kinds - a
  // book's post body is rendered directly from contentHtml (splitCoverImages/
  // m.trust above), never through a real CommentPost/Post component, so
  // neither of the two core extension points that normally handle these ever
  // fire for it (confirmed against both source files, not just reasoned
  // about): champis-forum's own showPostImagesInLightbox.js hooks
  // CommentPost's oncreate, and fof/upload's own downloadButtonInteraction.ts
  // hooks Post's oncreate to wire up every [data-fof-upload-download-uuid]
  // download button's click - without it those buttons are inert <div>s with
  // no click behavior of their own at all, reported directly as "files that
  // don't open" (on champis-articles' own ArticlePage.js, which has the
  // exact same underlying gap - fixed there too, see that file's own copy of
  // both methods below for the fuller comments).
  handleBookClick(e, discussion, post) {
    this.openImageLightbox(e);
    this.openFileDownload(e, discussion, post);
  }

  // Opens a book's own cover photo(s) (pulled out of its post body above,
  // see splitCoverImages()) - or any inline image still left in the body
  // text - in champis-forum's PostImageLightbox, matching a regular
  // discussion's own click-to-zoom lightbox (requested directly). Scoped to
  // one book's own ChampisBooksList-item (this method's own e.currentTarget)
  // rather than the whole page, so each book is its own gallery instead of
  // paging through every other book's covers too. Same DOM-scan-at-click-
  // time approach (and the same reasoning for why that's correct here, no
  // PostStream-style windowing to worry about) and the same
  // window.flarum.reg.get(...) cross-extension lookup (extensions can't
  // import each other's uncompiled JS source directly) as champis-articles'
  // own ArticlePage.js - see that file's own copy of this method for the
  // fuller version of both comments.
  openImageLightbox(e) {
    const target = e.target;
    const link = target.closest('.FoFUpload--Upl-Image-Preview-Link');
    if (!link) return;
    const PostImageLightbox = window.flarum.reg.get('champis-forum', 'forum/components/PostImageLightbox');
    if (!PostImageLightbox) return;
    e.preventDefault();
    const currentTarget = e.currentTarget;
    const links = Array.from(currentTarget.querySelectorAll('.FoFUpload--Upl-Image-Preview-Link'));
    const images = links.map(l => ({
      url: l.href,
      alt: l.querySelector('img')?.alt || '',
      caption: l.closest('figure')?.querySelector('figcaption')?.textContent || null
    }));
    app.modal.show(PostImageLightbox, {
      images,
      index: links.indexOf(link)
    });
  }

  // Same URL-building and permission gating as fof/upload's own
  // downloadButtonInteraction.ts - see champis-articles' own ArticlePage.js
  // copy of this method for the fuller comment.
  openFileDownload(e, discussion, post) {
    const target = e.target.closest('[data-fof-upload-download-uuid]');
    if (!target) return;
    e.preventDefault();
    const canDownload = app.forum.attribute('fof-upload.canDownload');
    const canDownloadInTag = discussion.attribute('canDownloadFiles');
    const denied = !canDownload || canDownloadInTag === false;
    if (denied) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('fof-upload.forum.states.unauthorized', {}, true));
      return;
    }
    const uuid = target.dataset.fofUploadDownloadUuid || '';
    const url = app.forum.attribute('apiUrl') + '/fof/download/' + encodeURIComponent(uuid) + '/' + encodeURIComponent(post && post.id() || '') + '/' + encodeURIComponent(app.session.csrfToken ?? '');
    window.open(url);
  }
}
flarum.reg.add('champis-others', 'forum/components/BooksPage', BooksPage);